// src/Frameworks/UnifiedApp.jsx

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import CommunityPanel from "./components/CommunityPanel";
import AssistantPanel from "./components/AssistantPanel";
import HexMapCanvas from "./components/HexMapCanvas";
import LeftRailTabs from "./LeftRailTabs";
import DialogHistory from "./DialogHistory";
import LoadingIndicator from "../components/LoadingIndicator";
import { BillingProvider, useBilling } from "./billing/BillingClient";

import useMindmapSession from "./state/useMindmapSession";
import "./styles/mindmap.css";

// Onboarding (ported from Frameworks.js)
import OnboardingQuestionsModal from "../pages/onboardingQuestions";
import WelcomeVideoModal from "../components/WelcomeVideoModal";
import api from "../api.js";

const API = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

// LocalStorage keys
const LS_ACTIVE_TAB_ID = "vybly.activeTabId";
const LS_ACTIVE_SESSION_ID = "vybly.activeSessionId";
const LS_ACTIVE_UPDATED_AT = "vybly.activeUpdatedAt";

// ---------- helpers ----------
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

function extractSid(obj) {
  return (
    obj?.session_id ||
    obj?.sessionId ||
    obj?.session?.session_id ||
    null
  );
}

function looksEmptySessionPayload(data) {
  const p = data?.payload || data || {};
  const hasSummary =
    (typeof p.rolling_summary_updated === "string" && p.rolling_summary_updated.trim()) ||
    (typeof p.rolling_summary === "string" && p.rolling_summary.trim());
  const hasBody = typeof p.body === "string" && p.body.trim();
  const hasUI = typeof p.ui === "string" && p.ui.length > 0;
  const hasChips = Array.isArray(p.chips) && p.chips.length > 0;
  const hasLatest = !!p.latest_exchange;
  const hasTurns =
    (Array.isArray(p.turns) && p.turns.length > 0) ||
    (typeof p.turnCount === "number" && p.turnCount > 0);
  return !(hasSummary || hasBody || hasUI || hasChips || hasLatest || hasTurns);
}

// ─────────────────────────────────────────────────────────────
// OUTER PROVIDER WRAPPER so Billing context exists
// ─────────────────────────────────────────────────────────────
export default function UnifiedApp() {
  return (
    <BillingProvider
      onWarning={(msg) =>
        window.dispatchEvent(
          new CustomEvent("vybly-toast", { detail: { type: "warning", msg } })
        )
      }
      onExpired={() =>
        window.dispatchEvent(
          new CustomEvent("vybly-toast", { detail: { type: "info", msg: "Session ended" } })
        )
      }
    >
      <UnifiedAppInner />
    </BillingProvider>
  );
}

// ─────────────────────────────────────────────────────────────
// INNER APP (your original logic; now wired to billing)
// ─────────────────────────────────────────────────────────────
function UnifiedAppInner() {
  const {
    payload,
    graph,
    loading,
    turning,
    error,
    submitText: submitTextCore,
    submitChip: submitChipCore,
    rehydrateSession,
    startNewSession,
  } = useMindmapSession();

  // Billing state/actions exposed by BillingClient.jsx
  const { activeWindow, credits, openPaymentModal, startSession } = useBilling();

  const [isStartingNew, setIsStartingNew] = useState(false);
  const busy = loading || turning || isStartingNew;

  // ── Onboarding flow (video → questions), ported from Frameworks.js ──
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [hasCheckedIntro, setHasCheckedIntro] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [activeTabId, setActiveTabId] = useState(() => {
    const v = localStorage.getItem(LS_ACTIVE_TAB_ID);
    return v ? Number(v) : null;
  });

  const [tabsEpoch, setTabsEpoch] = useState(0);
  const hydrateSeq = useRef(0);

  const liveSessionId =
    (payload && (payload.sessionId || payload.session_id)) ||
    localStorage.getItem(LS_ACTIVE_SESSION_ID) ||
    null;

  useEffect(() => {
    const hookSid = payload && (payload.sessionId || payload.session_id);
    const lsSid = localStorage.getItem(LS_ACTIVE_SESSION_ID);
    if (hookSid && hookSid !== lsSid) {
      localStorage.setItem(LS_ACTIVE_SESSION_ID, String(hookSid));
    }
  }, [payload]);

  // ---- Submit wrappers: if no session -> pay/start; if 402 mid-turn -> pay/start
  const submitText = useCallback(
    async (text) => {
      try { localStorage.setItem("vybly.lastUserText", String(text || "")); } catch {}

      if (!activeWindow) {
        // No active session: either consume a credit or open Stripe modal
        if ((credits ?? 0) > 0) {
          await startSession();        // consume credit + open 1h
        } else {
          await openPaymentModal();    // opens Stripe Payment Element; then starts session
        }
        return;
      }

      try {
        const res = await submitTextCore(text);
        localStorage.setItem(LS_ACTIVE_UPDATED_AT, String(Date.now()));
        return res;
      } catch (e) {
        const msg = (e && (e.message || e.toString())) || "";
        if ((e && e.status === 402) || msg.includes("402") || msg.toLowerCase().includes("payment required")) {
          if ((credits ?? 0) > 0) {
            await startSession();
          } else {
            await openPaymentModal();
          }
          return;
        }
        throw e;
      }
    },
    [submitTextCore, activeWindow, credits, startSession, openPaymentModal]
  );

  const submitChip = useCallback(
    async (chip) => {
      if (!activeWindow) {
        if ((credits ?? 0) > 0) {
          await startSession();
        } else {
          await openPaymentModal();
        }
        return;
      }
      try {
        const res = await submitChipCore(chip);
        localStorage.setItem(LS_ACTIVE_UPDATED_AT, String(Date.now()));
        return res;
      } catch (e) {
        const msg = (e && (e.message || e.toString())) || "";
        if ((e && e.status === 402) || msg.includes("402") || msg.toLowerCase().includes("payment required")) {
          if ((credits ?? 0) > 0) {
            await startSession();
          } else {
            await openPaymentModal();
          }
          return;
        }
        throw e;
      }
    },
    [submitChipCore, activeWindow, credits, startSession, openPaymentModal]
  );

  // Rehydrate or create a new session on load
  useEffect(() => {
    const sid = localStorage.getItem(LS_ACTIVE_SESSION_ID);
    if (sid) {
      fetch(`${API}/frameworks/mindmap/session/${sid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        credentials: "include",
      })
        .then((r) => {
          if (!r.ok) throw new Error(`rehydrate failed (${r.status})`);
          return r.json();
        })
        .then((data) => rehydrateSession(data))
        .catch(() => {
          localStorage.removeItem(LS_ACTIVE_TAB_ID);
          localStorage.removeItem(LS_ACTIVE_SESSION_ID);
          localStorage.removeItem(LS_ACTIVE_UPDATED_AT);
          setActiveTabId(null);
          startNewSession();
        });
    } else {
      startNewSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    // ── Intro video status check (copied from Frameworks.js) ──
  useEffect(() => {
    async function checkIntroSeen() {
      try {
        const userId = localStorage.getItem("user_id");
        if (!userId) { setHasCheckedIntro(true); return; }

        const res = await api.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/intro_status/?user_id=${userId}`
        );
        const seenIntro = res.data?.has_seen_intro_video;

        // Treat any falsy/string/number variants as "unseen" (Frameworks.js behavior)
        const isUnseen =
          seenIntro === false ||
          seenIntro === null ||
          seenIntro === undefined ||
          seenIntro === "f" ||
          seenIntro === "false" ||
          seenIntro === "False" ||
          seenIntro === 0;

        // Also honor our localStorage gate for "vybly.seenIntroVideo"
        if (isUnseen && localStorage.getItem("vybly.seenIntroVideo") !== "1") {
          setShowWelcomeVideo(true);
        }
      } catch (e) {
        console.error("Intro status fetch error:", e);
      } finally {
        setHasCheckedIntro(true);
      }
    }
    checkIntroSeen();
  }, []);

  // Close the video → set backend flag and localStorage, then open questions immediately
  const handleWelcomeVideoClose = async () => {
    setShowWelcomeVideo(false);
    try {
      localStorage.setItem("vybly.seenIntroVideo", "1");
      const userId = localStorage.getItem("user_id");
      if (userId) {
        await api.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/set_intro_seen`,
          { user_id: userId }
        );
      }
    } catch (e) {
      // Optionally log error
    } finally {
      // Transition to questions phase per the brief
      setShowOnboarding(true);
    }
  };

  // ── Questions-complete check (mirrors Frameworks.js) ──
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await api.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/questionsAnswered`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const alreadyAnswered = !!res.data?.allAnswered;
        const locallyComplete = localStorage.getItem("vybly.onboardingComplete") === "1";

        if (!alreadyAnswered && !locallyComplete) {
          setShowOnboarding(true);
        }
      } catch (e) {
        // Optionally handle/log error
      }
    };

    // Avoid double-show while the video is up; run after intro check
    if (!showWelcomeVideo) {
      checkOnboarding();
    }
  }, [showWelcomeVideo]);

  const handleOnboardingClose = () => {
    try { localStorage.setItem("vybly.onboardingComplete", "1"); } catch {}
    setShowOnboarding(false);

    // Force the tabs rail to refetch now…
    setTabsEpoch((n) => n + 1);
    // …and once more shortly after, since the server may create tabs async
    setTimeout(() => setTabsEpoch((n) => n + 1), 5000);
  };

  // -------- Tab activation (with loader + tab refresh) --------
  const handleTabActivated = useCallback(
    async (tab) => {
      if (!tab) return;

      setIsStartingNew(true); // show loader during auto-save + hydrate
      try {
        const activityPresent =
          localStorage.getItem(LS_ACTIVE_UPDATED_AT) ||
          (payload &&
            typeof payload.rolling_summary === "string" &&
            payload.rolling_summary.trim().length > 0) ||
          (graph && (graph.turnCount || graph.turns?.length));

        // If floating and there’s activity, auto-save it as a new tab
        if (activeTabId === null && activityPresent) {
          const sid =
            (payload && (payload.sessionId || payload.session_id)) ||
            localStorage.getItem(LS_ACTIVE_SESSION_ID);

          if (sid) {
            const summary =
              payload?.rolling_summary_updated ||
              payload?.rolling_summary ||
              "";

            if (summary.trim().length > 0) {
              const genRes = await fetch(`${API}/tabs/generate-tab/mindmap`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders(),
                },
                credentials: "include",
                body: JSON.stringify({
                  input_content: summary,
                  onboarding_answers: [],
                }),
              });
              if (!genRes.ok)
                throw new Error(`generate-tab failed (${genRes.status})`);
              const gen = await genRes.json();

              const normalizedTab = {
                domain: Array.isArray(gen.domain) ? gen.domain[0] : gen.domain,
                intent: Array.isArray(gen.intent) ? gen.intent[0] : gen.intent,
                barrier: gen.barrier || [],
                stage: gen.stage || [],
                relational_context: gen.relational_context || [],
                emotions: gen.emotions || [],
                custom_label: gen.custom_label || "",
              };

              const saveRes = await fetch(`${API}/tabs/save`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders(),
                },
                credentials: "include",
                body: JSON.stringify({
                  tab: normalizedTab,
                  closed: false,
                  session_id: sid,
                }),
              });
              if (!saveRes.ok)
                throw new Error(`save-tab failed (${saveRes.status})`);
              await saveRes.json();

              // ✅ Refresh rail so new tab shows up immediately
              setTabsEpoch((n) => n + 1);
            }
          }
        }

        // Activate the clicked tab
        localStorage.setItem(LS_ACTIVE_TAB_ID, String(tab.id));
        setActiveTabId(Number(tab.id));

        const seq = ++hydrateSeq.current;
        const r = await fetch(`${API}/frameworks/mindmap/session/by-tab`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({ tab_id: tab.id }),
        });
        if (!r.ok) throw new Error(`rehydrate by-tab failed (${r.status})`);
        const data = await r.json();
        if (seq !== hydrateSeq.current) return;

        const sid = extractSid(data);
        if (sid) {
          localStorage.setItem(LS_ACTIVE_SESSION_ID, String(sid));
          localStorage.setItem(LS_ACTIVE_UPDATED_AT, String(Date.now()));
        }
        rehydrateSession(data);
      } catch (e) {
        console.error("handleTabActivated error:", e);
      } finally {
        setIsStartingNew(false); // hide loader
      }
    },
    [activeTabId, payload, graph, rehydrateSession]
  );

  const handleTabClosed = useCallback(
    (closedTabId) => {
      const current = localStorage.getItem(LS_ACTIVE_TAB_ID);
      if (current && Number(current) === Number(closedTabId)) {
        localStorage.removeItem(LS_ACTIVE_TAB_ID);
        localStorage.removeItem(LS_ACTIVE_SESSION_ID);
        localStorage.removeItem(LS_ACTIVE_UPDATED_AT);
        setActiveTabId(null);
        startNewSession();
      }
      setTabsEpoch((n) => n + 1);
    },
    [startNewSession]
  );

  const hasActivity = useMemo(() => {
    if (localStorage.getItem(LS_ACTIVE_UPDATED_AT)) return true;
    if (
      payload &&
      typeof payload.rolling_summary === "string" &&
      payload.rolling_summary.trim().length > 0
    )
      return true;
    if (graph && (graph.turnCount || graph.turns?.length)) return true;
    return false;
  }, [payload, graph]);

  const canStartNew = !!liveSessionId && hasActivity && !busy;

  // -------- Start new thoughts flow (unchanged) --------
  const handleStartNewThoughts = useCallback(async () => {
    const sid =
      (payload && (payload.sessionId || payload.session_id)) ||
      localStorage.getItem(LS_ACTIVE_SESSION_ID);

    if (!sid || !hasActivity) return;

    setIsStartingNew(true);
    try {
      if (activeTabId === null) {
        const summary =
          payload?.rolling_summary_updated ||
          payload?.rolling_summary ||
          "";
        if (summary.trim().length > 0) {
          const genRes = await fetch(`${API}/tabs/generate-tab/mindmap`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            credentials: "include",
            body: JSON.stringify({
              input_content: summary,
              onboarding_answers: [],
            }),
          });
          if (!genRes.ok) throw new Error(`generate-tab failed (${genRes.status})`);
          const gen = await genRes.json();

          const normalizedTab = {
            domain: Array.isArray(gen.domain) ? gen.domain[0] : gen.domain,
            intent: Array.isArray(gen.intent) ? gen.intent[0] : gen.intent,
            barrier: gen.barrier || [],
            stage: gen.stage || [],
            relational_context: gen.relational_context || [],
            emotions: gen.emotions || [],
            custom_label: gen.custom_label || "",
          };

          const saveRes = await fetch(`${API}/tabs/save`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            credentials: "include",
            body: JSON.stringify({
              tab: normalizedTab,
              closed: false,
              session_id: sid,
            }),
          });
          if (!saveRes.ok) throw new Error(`save-tab failed (${saveRes.status})`);
          const saved = await saveRes.json();

          if (saved.tab_id) {
            localStorage.setItem(LS_ACTIVE_TAB_ID, String(saved.tab_id));
            setActiveTabId(saved.tab_id);
          }

          setTabsEpoch((n) => n + 1);
        }
      }
    } catch (e) {
      console.warn("tab creation/save failed:", e);
    } finally {
      setIsStartingNew(false);
    }

    localStorage.removeItem(LS_ACTIVE_TAB_ID);
    localStorage.removeItem(LS_ACTIVE_SESSION_ID);
    localStorage.removeItem(LS_ACTIVE_UPDATED_AT);
    setActiveTabId(null);
    startNewSession();
  }, [activeTabId, API, hasActivity, payload, startNewSession]);

  return (
    <BillingProvider onExpired={handleStartNewThoughts}>
      <div className="frameworks-shell">
        {busy && (
          <div className="loading-overlay" aria-busy="true" aria-live="polite">
            <LoadingIndicator />
          </div>
        )}
                {/* ── Onboarding modals ── */}
        {hasCheckedIntro && showWelcomeVideo && (
          <WelcomeVideoModal
            src={process.env.REACT_APP_ONBOARDING_VIDEO_URL}
            poster="/images/welcome-poster.jpg"
            onClose={handleWelcomeVideoClose}
          />
        )}

        {showOnboarding && !showWelcomeVideo && (
          <OnboardingQuestionsModal
            onClose={handleOnboardingClose}
          />
        )}


    <div className="canvas-row">
      <div className="canvas-stack">
        <HexMapCanvas graph={graph} />
      </div>

      <div className="assistant-panel glass-overlay">
        <AssistantPanel
          payload={payload}
          onSubmitText={submitText}
          onSubmitChip={submitChip}
          busy={busy}
          // Drive the single CTA button’s behavior:
          hasActiveSession={!!activeWindow}
          hasCredits={(credits ?? 0) > 0}
          onStartSession={startSession}
          onBuyAndStart={openPaymentModal}
          // Prevent typing when no session is active (clarity)
          disableInput={!activeWindow}
        />

        <div className="assistant-footer">
          <button
            type="button"
            className="vybly-button secondary"
            onClick={handleStartNewThoughts}
            disabled={!canStartNew}
          >
            Start new thoughts
          </button>
        </div>
      </div>
    </div>

    {/* Community Map + Vybes */}
    <CommunityPanel />

    {/* Your Tabs + Dialog History side by side */}
    <div className="community-panel-grid">
      {/* Left: Your Tabs */}
      <div className="community-card">
        <LeftRailTabs
          key={tabsEpoch}
          activeTabId={activeTabId}
          onActivate={handleTabActivated}
          onClosed={handleTabClosed}
        />
      </div>

      {/* Right: Dialog History */}
      <div className="community-card">
        <DialogHistory
          sessionId={
            (payload && (payload.sessionId || payload.session_id)) ||
            localStorage.getItem("vybly.activeSessionId") ||
            null
          }
          latestAssistantBody={
            payload?.body || payload?.ui_payload?.body || ""
          }
          latestExchange={{
            user_text:
              payload?.latest_exchange?.user_text ??
              payload?.latestExchange?.user_text ??
              "",
            assistant_text: null // provided separately as latestAssistantBody
          }}
          turnBump={
            (typeof graph?.turnCount === "number" ? graph.turnCount : undefined) ??
            (Array.isArray(graph?.turns) ? graph.turns.length : 0)
          }
        />
      </div>
    </div>

    {error && (
      <div className="error-banner" role="alert">
        {error}
      </div>
    )}
      </div> {/* closes .frameworks-shell */}
    </BillingProvider> 
  );
}
